import React, { Component } from "react";

interface Props {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.setState({ errorInfo });
        // Burada hata raporlama servisi entegre edilebilir (Sentry, LogRocket vb.)
        console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    handleReload = (): void => {
        window.location.reload();
    };

    handleGoHome = (): void => {
        window.location.href = "/";
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        <div style={styles.iconWrapper}>
                            <svg
                                width="80"
                                height="80"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                style={{ color: "#f59e0b" }}
                            >
                                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h1 style={styles.title}>Bir Şeyler Ters Gitti</h1>
                        <p style={styles.description}>
                            Üzgünüz, beklenmedik bir hata oluştu. Lütfen sayfayı yenileyin veya ana sayfaya dönün.
                        </p>
                        {process.env.NODE_ENV === "development" && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Hata Detayları</summary>
                                <pre style={styles.errorText}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                        <div style={styles.actions}>
                            <button onClick={this.handleReload} style={styles.primaryButton}>
                                Sayfayı Yenile
                            </button>
                            <button onClick={this.handleGoHome} style={styles.secondaryButton}>
                                Ana Sayfaya Dön
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(150deg, #0b0d14 0%, #0a0c12 45%, #050609 100%)",
        padding: "2rem",
        fontFamily: "'Inter', system-ui, sans-serif"
    },
    content: {
        maxWidth: "480px",
        textAlign: "center" as const,
        color: "#f4f6fb"
    },
    iconWrapper: {
        marginBottom: "1.5rem"
    },
    title: {
        fontSize: "1.75rem",
        fontWeight: 600,
        marginBottom: "1rem",
        color: "#f4f6fb"
    },
    description: {
        fontSize: "1rem",
        lineHeight: 1.6,
        color: "rgba(244, 246, 251, 0.7)",
        marginBottom: "1.5rem"
    },
    details: {
        marginBottom: "1.5rem",
        textAlign: "left" as const
    },
    summary: {
        cursor: "pointer",
        color: "#f59e0b",
        marginBottom: "0.5rem"
    },
    errorText: {
        background: "rgba(0, 0, 0, 0.3)",
        padding: "1rem",
        borderRadius: "8px",
        fontSize: "0.75rem",
        overflow: "auto",
        maxHeight: "200px",
        color: "#ef4444"
    },
    actions: {
        display: "flex",
        gap: "1rem",
        justifyContent: "center",
        flexWrap: "wrap" as const
    },
    primaryButton: {
        padding: "0.75rem 1.5rem",
        borderRadius: "999px",
        border: "none",
        background: "linear-gradient(135deg, #B49B57, #C8A7E8)",
        color: "#0b0c11",
        fontWeight: 600,
        fontSize: "0.95rem",
        cursor: "pointer",
        transition: "transform 0.3s ease, box-shadow 0.3s ease"
    },
    secondaryButton: {
        padding: "0.75rem 1.5rem",
        borderRadius: "999px",
        border: "1px solid rgba(255, 255, 255, 0.25)",
        background: "rgba(255, 255, 255, 0.1)",
        color: "#f4f6fb",
        fontWeight: 500,
        fontSize: "0.95rem",
        cursor: "pointer",
        transition: "background 0.3s ease"
    }
};
